import { useMemo, useState, type JSX } from 'react';
import { LEAVE_TYPES, isHalfDay, type LeaveType } from '@dxtech/shared';
import { Modal } from '../ui/common.js';
import { api } from '../api/client.js';
import { dateRangeError } from '../lib/validateRange.js';

function computeDays(type: LeaveType, start: string, end: string): number {
  if (isHalfDay(type)) return 0.5;
  const a = new Date(start), b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

export function LeaveModal({ remaining, onClose, onDone }: {
  remaining: number; onClose: () => void; onDone: (msg: string) => void;
}): JSX.Element {
  const todayStr = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Seoul' }).format(new Date());
  const [type, setType] = useState<LeaveType>('연차');
  const [start, setStart] = useState(todayStr);
  const [end, setEnd] = useState(todayStr);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const half = isHalfDay(type);
  const days = useMemo(() => computeDays(type, start, half ? start : end), [type, start, end, half]);
  const warn = days > remaining;
  const rangeErr = half ? null : dateRangeError(start, end);

  const submit = async (): Promise<void> => {
    if (rangeErr || warn || days <= 0) return;
    setBusy(true);
    try {
      await api.createLeave({ type, startDate: start, endDate: half ? start : end, reason });
      onDone('휴가 신청이 상신되었습니다');
      onClose();
    } catch (e) {
      onDone(e instanceof Error ? e.message : '신청 실패');
      setBusy(false);
    }
  };

  return (
    <Modal width={460} onClose={onClose}>
      <div style={{ padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 18 }}>휴가 신청</div>

        <div style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 8 }}>휴가 종류</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginBottom: 16 }}>
          {LEAVE_TYPES.map((t) => (
            <button key={t} onClick={() => setType(t)} style={chip(type === t)}>{t}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
          <label style={field}><span style={lbl}>시작일</span><input type="date" className="dx-input" value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label style={field}><span style={lbl}>종료일</span>
            <input type="date" className="dx-input" value={half ? start : end} disabled={half}
              style={half ? { background: '#F3F4F6', color: '#9CA3AF' } : undefined}
              onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>

        {rangeErr && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '8px 11px', fontSize: 12.5, fontWeight: 600, marginBottom: 14 }}>{rangeErr}</div>
        )}

        <div style={{ background: '#F9FAFB', border: '1px solid #EDEFF2', borderRadius: 8, padding: '12px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: 13, color: '#6B7280', fontWeight: 600 }}>사용 일수</span>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#2563EB' }} className="tnum">{days}일</span>
        </div>

        {warn && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#B91C1C', borderRadius: 8, padding: '10px 12px', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
            잔여 연차({remaining}일)를 초과합니다.
          </div>
        )}

        <textarea className="dx-textarea" rows={3} placeholder="사유" value={reason} onChange={(e) => setReason(e.target.value)} />

        <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
          <button className="dx-btn dx-btn-ghost" style={{ flex: 1 }} onClick={onClose}>취소</button>
          <button className="dx-btn dx-btn-primary" style={{ flex: 1 }} disabled={busy || !!rangeErr || warn || days <= 0} onClick={submit}>신청</button>
        </div>
      </div>
    </Modal>
  );
}

const field = { flex: 1, display: 'flex', flexDirection: 'column', gap: 6 } as const;
const lbl = { fontSize: 12, fontWeight: 600, color: '#6B7280' } as const;
const chip = (active: boolean) => ({
  border: `1px solid ${active ? '#2563EB' : '#D1D5DB'}`, background: active ? '#EFF4FE' : '#fff',
  color: active ? '#1D4ED8' : '#4B5563', fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
  padding: '7px 13px', borderRadius: 7, cursor: 'pointer',
});
